import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

import { FlashbarItem } from 'src/components/WhatsNew/FeatureType';
import { fetchFlashbarItems } from 'src/services/flashbarService';
import { getCognitoAuthSession } from 'src/authentication/cognito';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { getBindleLockForNodeType, getBindleLockAuthorizationRequest } from 'src/authentication/bindle-utils';
import { logger } from 'src/utils/logger';

import { GlobalAlertState } from './globalAlertStore.interface';

const DISMISSED_ALERTS_KEY = 'snow_global_alerts_dismissed';

const getDismissedIds = (): string[] => {
	try {
		const stored = localStorage.getItem(DISMISSED_ALERTS_KEY);
		return stored ? JSON.parse(stored) : [];
	} catch {
		return [];
	}
};

const OVERRIDE_KEY = 'snow_global_execution_override';
const OVERRIDE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface OverrideEntry {
	alertId: string;
	timestamp: number;
}

const getOverriddenAlertId = (): string | null => {
	try {
		const stored = localStorage.getItem(OVERRIDE_KEY);
		if (!stored) return null;

		const entry: OverrideEntry = JSON.parse(stored);
		if (Date.now() - entry.timestamp > OVERRIDE_TTL_MS) {
			localStorage.removeItem(OVERRIDE_KEY);

			return null;
		}

		return entry.alertId;
	} catch {
		return null;
	}
};

const saveOverride = (alertId: string) => {
	const entry: OverrideEntry = { alertId, timestamp: Date.now() };
	localStorage.setItem(OVERRIDE_KEY, JSON.stringify(entry));
};

const clearOverride = () => {
	localStorage.removeItem(OVERRIDE_KEY);
};

const saveDismissedIds = (ids: string[]) => {
	localStorage.setItem(DISMISSED_ALERTS_KEY, JSON.stringify(ids));
};

const computeBlockingState = (alerts: FlashbarItem[]) => {
	const blockingAlert = alerts.find((a) => a.isBlockingExecution) || null;
	const isExecutionBlocked = !!blockingAlert;

	return { blockingAlert, isExecutionBlocked };
};

const initialOverrideId = getOverriddenAlertId();

export const useGlobalAlertStore = create<GlobalAlertState>((set, get) => ({
	alerts: [],
	isExecutionBlocked: false,
	blockingAlert: null,
	canUserOverride: false,
	hasOverridden: !!initialOverrideId,
	overriddenAlertId: initialOverrideId,
	isLoading: false,
	isFetched: false,
	dismissedAlertIds: getDismissedIds(),

	fetchAlerts: async () => {
		if (get().isFetched || get().isLoading) return;

		set({ isLoading: true });

		try {
			const allItems = await fetchFlashbarItems();
			const alerts = allItems.filter((item) => item.isGlobal);
			const { blockingAlert, isExecutionBlocked } = computeBlockingState(alerts);

			set({
				alerts,
				blockingAlert,
				isExecutionBlocked,
				isLoading: false,
				isFetched: true,
			});

			const previousOverrideId = getOverriddenAlertId();

			if (blockingAlert && previousOverrideId === blockingAlert.id) {
				set({ hasOverridden: true, overriddenAlertId: previousOverrideId });
			} else {
				// Override doesn't match current blocking alert — clear stale override
				if (previousOverrideId) {
					clearOverride();
				}
				set({ hasOverridden: false, overriddenAlertId: null });
			}

			// Auto-check override permission if there's a blocking alert
			if (isExecutionBlocked) {
				try {
					const session = await getCognitoAuthSession();
					const rawUsername: string | undefined = session?.accessToken?.payload?.username?.split('_')[1];

					if (rawUsername) {
						await get().checkOverridePermission(rawUsername);
					}
				} catch (error) {
					logger.error(`Failed to auto-check override permission [ error ] ::: [ ${JSON.stringify(error)} ]`);
				}
			}
		} catch (error) {
			logger.error(`Failed to fetch global alerts [ error ] ::: [ ${JSON.stringify(error)} ]`);
			set({ isLoading: false, isFetched: true });
		}
	},

	checkOverridePermission: async (userName: string) => {
		if (!userName) {
			set({ canUserOverride: false });

			return;
		}

		try {
			const lockId = getBindleLockForNodeType('snow_team_internal');
			const response = await callSNOWDataService({
				path: '/authorize',
				body: {
					user: userName,
					resource: getBindleLockAuthorizationRequest([lockId]),
					requestId: uuidv4(),
				},
				method: 'POST',
			});

			const isAuthorized =
				response?.authorizedResource?.ids &&
				response.authorizedResource.ids.length > 0;

			set({ canUserOverride: isAuthorized });
		} catch (error) {
			logger.error(`Failed to check override permission [ userName ] ::: [ ${userName} ] [ error ] ::: [ ${JSON.stringify(error)} ]`);
			set({ canUserOverride: false });
		}
	},

	dismissAlert: (alertId: string) => {
		const { alerts, dismissedAlertIds } = get();
		const alert = alerts.find((a) => a.id === alertId);

		if (!alert) return;

		if (alert.isBlockingExecution) return;

		const updatedDismissedIds = [...dismissedAlertIds, alertId];
		saveDismissedIds(updatedDismissedIds);

		set({ dismissedAlertIds: updatedDismissedIds });
	},

	overrideExecution: () => {
		const { canUserOverride, blockingAlert } = get();

		if (!canUserOverride || !blockingAlert) return;

		saveOverride(blockingAlert.id);

		set({ hasOverridden: true, overriddenAlertId: blockingAlert.id });
	},

	refresh: async () => {
		set({ isFetched: false, isLoading: false, hasOverridden: false, overriddenAlertId: null });

		await get().fetchAlerts();
	},
}));
